import { useFormContext } from 'react-hook-form';
import { WorkflowNode } from './FormWorkflowView';
import { FormSequenceView } from './FormSequenceView';
import { ReactElement, useEffect, useMemo } from 'react';

interface FormWorkflowNodeProps<T extends object> {
  workflowNode: Omit<WorkflowNode<T>, 'validationSchema'> & { nodeIndex: number };
  name?: string;
  index: number;
  totalNodes: number;
  handleNodeError: (traversedIndex: number, nodeIndex: number) => void;
}

export const FormWorkflowNode = <T extends object>({ workflowNode, name, index, totalNodes, handleNodeError }: FormWorkflowNodeProps<T>) => {
  const { formState, watch } = useFormContext<T>();
  const formValues = watch();

  const fieldName = name ?? (workflowNode.element as ReactElement<any>).props.name ?? null;
  const errors = formState.errors[fieldName as keyof T] as any;

  useEffect(() => {
    if (fieldName && errors && workflowNode.breakRenderingOnInvalid) {
      handleNodeError(index, workflowNode.nodeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const calculatedChildren = useMemo(() => {
    if (workflowNode.children) {
      const childNode = workflowNode.children(formValues);
      if (childNode) {
        return childNode;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, workflowNode]);
  return (
    <>
      <FormSequenceView
        key={index}
        forceIndicatorType={workflowNode.indicatorType}
        title={workflowNode.title}
        isFirst={index === 0}
        isLast={!calculatedChildren && index === totalNodes - 1}
        fieldErrors={errors}
        fieldName={fieldName}
        classname={workflowNode.classname}
      >
        {workflowNode.element}
      </FormSequenceView>
      {calculatedChildren && (
        <FormSequenceView
          key={`${index}-child`}
          isFirst={false}
          isLast={index === totalNodes - 1}
          fieldErrors={errors}
          fieldName={fieldName}
          forceIndicatorType={calculatedChildren.indicatorType}
          title={calculatedChildren.title}
          classname={calculatedChildren.classname}
        >
          {calculatedChildren.element}
        </FormSequenceView>
      )}
    </>
  );
};
